import React, { useState, useEffect, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { supabase, isRecoveryRedirect } from './utils/supabase';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import AboutPage from './components/AboutPage';
import PricingPage from './components/PricingPage';
import BillingPage from './components/BillingPage';
import SettingsPage from './components/SettingsPage';
import AuthModal from './components/AuthModal';
import CommunityPage from './components/CommunityPage';
import PublicationsPage from './components/PublicationsPage';
import MyAnalysesPage from './components/MyAnalysesPage';
import LandingPage from './components/LandingPage';
import './App.css';

export type PageType = 'home' | 'myanalyses' | 'community' | 'publications' | 'about' | 'pricing' | 'billing' | 'settings';
export type AuthView = 'login' | 'signup' | 'forgot' | 'reset';

/** Page id ↔ URL path. Every page now has its own shareable / bookmarkable URL. */
export const PAGE_PATHS: Record<PageType, string> = {
  home: '/',
  myanalyses: '/my-analyses',
  community: '/community',
  publications: '/publications',
  about: '/about',
  pricing: '/pricing',
  billing: '/billing',
  settings: '/settings',
};

export type ViewerTab = 'map' | 'charts' | 'details';

/** Shareable permalink for one analysis card, optionally pinned to a tab:
    /community/<id>            → default (map) tab
    /community/<id>/charts     → charts tab
    /my-analyses/<id>/details  → details tab */
export const analysisPath = (
  sessionId: string,
  scope: 'community' | 'myanalyses' = 'community',
  tab?: ViewerTab,
) =>
  `${PAGE_PATHS[scope]}/${encodeURIComponent(sessionId)}${tab && tab !== 'map' ? `/${tab}` : ''}`;

/** Permalink for a whole conversation timeline:
    /my-analyses/c/<conversation_id>  → owner's view
    /community/c/<conversation_id>    → public view of a shared conversation */
export const conversationPath = (
  conversationId: string,
  scope: 'community' | 'myanalyses' = 'myanalyses',
) => `${PAGE_PATHS[scope]}/c/${encodeURIComponent(conversationId)}`;

export const parseViewerTab = (raw?: string): ViewerTab =>
  raw === 'charts' || raw === 'details' ? raw : 'map';

const pathToPage = (pathname: string): PageType => {
  const clean = pathname.replace(/\/+$/, '') || '/';
  const hit = (Object.keys(PAGE_PATHS) as PageType[]).find(
    p => PAGE_PATHS[p] === clean || (PAGE_PATHS[p] !== '/' && clean.startsWith(PAGE_PATHS[p] + '/')),
  );
  return hit ?? 'home';
};

const AppShell: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPage: PageType = pathToPage(location.pathname);
  const setCurrentPage = (page: string) => {
    const target = PAGE_PATHS[(page as PageType)] ?? '/';
    if (target !== location.pathname) navigate(target);
  };

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  const [authView, setAuthView] = useState<AuthView>('login');
  const [isFullPageView, setIsFullPageView] = useState<boolean>(false);
  // A recovery session is NOT a normal sign-in: it must show the
  // "choose a new password" form instead of dropping into the account.
  const isRecoveryRef = useRef<boolean>(false);

  useEffect(() => {
    // Black is the only theme. The `dark-mode` body class is kept because a
    // few component selectors still key off it.
    document.body.classList.add('dark-mode');
    localStorage.removeItem('darkMode');
    localStorage.removeItem('themeChosen');

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const enterRecovery = () => {
      isRecoveryRef.current = true;
      setAuthView('reset');
      setShowAuthModal(true);
    };

    // Recovery links arrive as  #access_token=...&type=recovery
    // This snapshot is taken at module load, before the Supabase client
    // consumes the fragment. Checked in addition to the event below, because
    // the event can fire before this listener is attached.
    if (isRecoveryRedirect) {
      enterRecovery();
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        // This previously fell through to the branch below, which closed the
        // modal and signed the user straight into their account without ever
        // offering the password form.
        setUser(session?.user ?? null);
        enterRecovery();
        return;
      }

      setUser(session?.user ?? null);
      if (session?.user && !isRecoveryRef.current) {
        setShowAuthModal(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Full-page analysis viewers are per-page; reset when the *page* changes
  // (not when only the /:sessionId segment changes — the page owns that).
  useEffect(() => {
    setIsFullPageView(false);
    window.scrollTo(0, 0);
  }, [currentPage]);

  // Accent colour is per section: yellow on the landing page (default
  // tokens), green on My Analyses + Research Community. See App.css.
  const accent: 'yellow' | 'green' =
    currentPage === 'myanalyses' || currentPage === 'community' ? 'green' : 'yellow';

  if (loading) {
    return (
      <div className="app-container loading-screen dark-mode" data-accent={accent}>
        <div className="loading-text">Loading...</div>
      </div>
    );
  }

  return (
    <div className="app-container dark-mode" data-accent={accent}>
      <Navigation
        user={user}
        setUser={setUser}
        setShowAuthModal={setShowAuthModal}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />

      <Routes>
        <Route
          path={PAGE_PATHS.home}
          element={
            <LandingPage
              user={user}
              setShowAuthModal={setShowAuthModal}
              setAuthView={setAuthView}
            />
          }
        />
        {/* `/my-analyses/:sessionId` and `/community/:sessionId` are permalinks
            to a single card; the page reads the param and opens the viewer. */}
        <Route
          path={`${PAGE_PATHS.myanalyses}/c/:conversationId`}
          element={
            <MyAnalysesPage
              user={user}
              setShowAuthModal={setShowAuthModal}
              setAuthView={setAuthView}
              setIsFullPageView={setIsFullPageView}
            />
          }
        />
        <Route
          path={`${PAGE_PATHS.myanalyses}/:sessionId?/:tab?`}
          element={
            <MyAnalysesPage
              user={user}
              setShowAuthModal={setShowAuthModal}
              setAuthView={setAuthView}
              setIsFullPageView={setIsFullPageView}
            />
          }
        />
        <Route
          path={`${PAGE_PATHS.community}/c/:conversationId`}
          element={<CommunityPage user={user} setIsFullPageView={setIsFullPageView} />}
        />
        <Route
          path={`${PAGE_PATHS.community}/:sessionId?/:tab?`}
          element={<CommunityPage user={user} setIsFullPageView={setIsFullPageView} />}
        />
        <Route path={PAGE_PATHS.publications} element={<PublicationsPage />} />
        <Route path={PAGE_PATHS.about} element={<AboutPage />} />
        <Route path={PAGE_PATHS.pricing} element={<PricingPage setCurrentPage={setCurrentPage} />} />
        <Route path={PAGE_PATHS.billing} element={<BillingPage user={user} />} />
        <Route path={PAGE_PATHS.settings} element={<SettingsPage user={user} />} />
        {/* Legacy / unknown paths → home */}
        <Route path="*" element={<Navigate to={PAGE_PATHS.home} replace />} />
      </Routes>

      {showAuthModal && (
        <AuthModal
          authView={authView}
          setAuthView={setAuthView}
          setShowAuthModal={(show: boolean) => {
            if (!show) {
              isRecoveryRef.current = false;
              // Strip the recovery token from the address bar so a refresh
              // doesn't re-trigger the flow.
              window.history.replaceState(
                null,
                '',
                window.location.pathname + window.location.search
              );
            }
            setShowAuthModal(show);
          }}
        />
      )}

      {!isFullPageView && <Footer />}
    </div>
  );
};

const App: React.FC = () => (
  <BrowserRouter>
    <AppShell />
  </BrowserRouter>
);

export default App;
